import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import NeoInput from "../../../../../components/Common/NeoInput";
import NeoButton from "../../../../../components/Common/NeoButton";
import NeoCard from "../../../../../components/Common/NeoCard";

const DetailGeneral = ({ tenant, setTenant, handleLogAction, setAlertMessage }) => {
    const [form, setForm] = useState({
        name: "",
        corporateName: "",
        document: "",
        observations: "",
        zipCode: "",
        street: "",
        number: "",
        neighborhood: "",
        city: "",
        state: "",
        responsibleName: "",
        responsibleEmail: "",
        phone: "",
        contactName: "",
        contactEmail: "",
        contactPhone: ""
    });

    useEffect(() => {
        if (tenant) {
            setForm({
                name: tenant.name || "",
                corporateName: tenant.corporateName || "",
                document: tenant.document || "",
                observations: tenant.observations || "",
                zipCode: tenant.zipCode || (tenant.address?.zipCode) || "",
                street: tenant.street || (tenant.address?.street) || "",
                number: tenant.number || (tenant.address?.number) || "",
                complement: tenant.complement || (tenant.address?.complement) || "",
                neighborhood: tenant.neighborhood || (tenant.address?.neighborhood) || "",
                city: tenant.city || (tenant.address?.city) || "",
                state: tenant.state || (tenant.address?.state) || "",
                responsibleName: tenant.responsibleName || tenant.ownerName || "",
                responsibleEmail: tenant.responsibleEmail || tenant.ownerEmail || "",
                phone: tenant.phone || "",
                contactName: tenant.contactName || "",
                contactEmail: tenant.contactEmail || "",
                contactPhone: tenant.contactPhone || ""
            });
        }
    }, [tenant]);

    const handleInputChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const isDirty =
        form.name !== (tenant.name || "") ||
        form.corporateName !== (tenant.corporateName || "") ||
        form.document !== (tenant.document || "") ||
        form.observations !== (tenant.observations || "") ||
        form.zipCode !== (tenant.zipCode || "") ||
        form.street !== (tenant.street || "") ||
        form.number !== (tenant.number || "") ||
        form.neighborhood !== (tenant.neighborhood || "") ||
        form.city !== (tenant.city || "") ||
        form.state !== (tenant.state || "") ||
        form.responsibleName !== (tenant.responsibleName || "") ||
        form.responsibleEmail !== (tenant.responsibleEmail || "") ||
        form.phone !== (tenant.phone || "") ||
        form.contactName !== (tenant.contactName || "") ||
        form.contactEmail !== (tenant.contactEmail || "") ||
        form.contactPhone !== (tenant.contactPhone || "");

    const handleSave = () => {
        setTenant((prev) => ({
            ...prev,
            ...form
        }));
        handleLogAction("tenant_profile_updated", "Atualizou informações cadastrais da empresa");
        setAlertMessage({ type: "success", text: "Informações da empresa salvas com sucesso!" });
    };

    return (
        <NeoCard
            title="Informações Cadastrais Detalhadas"
            headerActions={
                <NeoButton
                    variant="primary"
                    size="sm"
                    disabled={!isDirty}
                    onClick={handleSave}
                >
                    Salvar Alterações
                </NeoButton>
            }
            thinBorder={true}
            bodyClassName="p-4"
        >
            {/* SEÇÃO 1: DADOS CORPORATIVOS */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3" style={{ letterSpacing: "0.5px" }}>
                Dados Corporativos
            </h6>
            <Row className="g-3 mb-4">
                <Col md={6}>
                    <NeoInput
                        label="Nome Fantasia / Academia *"
                        placeholder="Ex: A2 Aquática"
                        value={form.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                    />
                </Col>
                <Col md={6}>
                    <NeoInput
                        label="Razão Social"
                        placeholder="Ex: A2 Aquática Fitness Ltda"
                        value={form.corporateName}
                        onChange={(e) => handleInputChange("corporateName", e.target.value)}
                    />
                </Col>
                <Col md={4}>
                    <NeoInput
                        label="CNPJ ou CPF *"
                        placeholder="00.000.000/0001-00"
                        value={form.document}
                        onChange={(e) => handleInputChange("document", e.target.value)}
                    />
                </Col>
            </Row>

            <hr />

            {/* SEÇÃO 2: ENDEREÇO DA EMPRESA */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 mt-4" style={{ letterSpacing: "0.5px" }}>
                Endereço da Empresa
            </h6>
            <Row className="g-3 mb-4">
                <Col md={3}>
                    <NeoInput
                        label="CEP *"
                        placeholder="00000-000"
                        value={form.zipCode}
                        onChange={(e) => handleInputChange("zipCode", e.target.value)}
                    />
                </Col>
                <Col md={6}>
                    <NeoInput
                        label="Rua / Logradouro *"
                        placeholder="Ex: Rua das Flores"
                        value={form.street}
                        onChange={(e) => handleInputChange("street", e.target.value)}
                    />
                </Col>
                <Col md={3}>
                    <NeoInput
                        label="Número *"
                        placeholder="Ex: 123"
                        value={form.number}
                        onChange={(e) => handleInputChange("number", e.target.value)}
                    />
                </Col>
                <Col md={4}>
                    <NeoInput
                        label="Bairro *"
                        placeholder="Ex: Centro"
                        value={form.neighborhood}
                        onChange={(e) => handleInputChange("neighborhood", e.target.value)}
                    />
                </Col>
                <Col md={5}>
                    <NeoInput
                        label="Cidade *"
                        placeholder="Ex: Aracaju"
                        value={form.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                    />
                </Col>
                <Col md={3}>
                    <NeoInput
                        label="Estado (UF) *"
                        placeholder="SE"
                        maxLength={2}
                        value={form.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                    />
                </Col>
            </Row>

            <hr />

            {/* SEÇÃO 3: RESPONSÁVEL PRINCIPAL */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 mt-4" style={{ letterSpacing: "0.5px" }}>
                Responsável Principal
            </h6>
            <Row className="g-3 mb-4">
                <Col md={5}>
                    <NeoInput
                        label="Nome do Responsável *"
                        placeholder="Ex: Felipe Macedo"
                        value={form.responsibleName}
                        onChange={(e) => handleInputChange("responsibleName", e.target.value)}
                    />
                </Col>
                <Col md={4}>
                    <NeoInput
                        label="E-mail do Responsável *"
                        type="email"
                        placeholder="Ex: responsavel@empresa.com"
                        value={form.responsibleEmail}
                        onChange={(e) => handleInputChange("responsibleEmail", e.target.value)}
                    />
                </Col>
                <Col md={3}>
                    <NeoInput
                        label="Telefone / WhatsApp *"
                        placeholder="Ex: (00) 90000-0000"
                        value={form.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                    />
                </Col>
            </Row>

            <hr />

            {/* SEÇÃO 4: CONTATO PRINCIPAL (SECUNDÁRIO) */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 mt-4" style={{ letterSpacing: "0.5px" }}>
                Contato Principal
            </h6>
            <Row className="g-3 mb-4">
                <Col md={5}>
                    <NeoInput
                        label="Nome do Contato Principal"
                        placeholder="Ex: Juliana Santos"
                        value={form.contactName}
                        onChange={(e) => handleInputChange("contactName", e.target.value)}
                    />
                </Col>
                <Col md={4}>
                    <NeoInput
                        label="E-mail do Contato"
                        type="email"
                        placeholder="Ex: contato@empresa.com"
                        value={form.contactEmail}
                        onChange={(e) => handleInputChange("contactEmail", e.target.value)}
                    />
                </Col>
                <Col md={3}>
                    <NeoInput
                        label="Telefone do Contato"
                        placeholder="Ex: (00) 90000-0000"
                        value={form.contactPhone}
                        onChange={(e) => handleInputChange("contactPhone", e.target.value)}
                    />
                </Col>
            </Row>

            <hr />

            {/* SEÇÃO 5: OBSERVAÇÕES */}
            <div className="mt-3">
                <NeoInput
                    label="Observações Internas (Uso Privado da Equipe)"
                    type="textarea"
                    rows={4}
                    value={form.observations}
                    onChange={(e) => handleInputChange("observations", e.target.value)}
                />
            </div>
        </NeoCard>
    );
};

DetailGeneral.propTypes = {
    tenant: PropTypes.object.isRequired,
    setTenant: PropTypes.func.isRequired,
    handleLogAction: PropTypes.func.isRequired,
    setAlertMessage: PropTypes.func.isRequired
};

export default DetailGeneral;
